/**
 * Product image gallery — slides, thumbnails, and pagination dots.
 */
class PmdGallery extends HTMLElement {
  #controller = new AbortController();
  #current = 0;
  #isRTL = false;

  connectedCallback() {
    this.#isRTL = document.documentElement.dir === 'rtl';

    const slides = this.querySelectorAll('[data-gallery-slide]');
    if (!slides.length) return;

    const { signal } = this.#controller;

    this.querySelectorAll('[data-gallery-thumb]').forEach((control) => {
      control.addEventListener(
        'click',
        () => {
          const idx = control.getAttribute('data-gallery-thumb');
          if (idx === null) return;
          this.#go(parseInt(idx, 10));
        },
        { signal },
      );
    });

    const prev = this.querySelector('[data-gallery-prev]');
    const next = this.querySelector('[data-gallery-next]');

    if (prev) {
      prev.addEventListener(
        'click',
        () => {
          this.#go(this.#current - 1);
        },
        { signal },
      );
    }

    if (next) {
      next.addEventListener(
        'click',
        () => {
          this.#go(this.#current + 1);
        },
        { signal },
      );
    }

    this.addEventListener(
      'keydown',
      (event) => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;

        event.preventDefault();
        const dir = this.#isRTL ? -1 : 1;
        const delta = event.key === 'ArrowRight' ? dir : -dir;
        this.#go(this.#current + delta);
      },
      { signal },
    );

    this.#go(0);
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  /**
   * @param {number} idx
   */
  #go(idx) {
    const slides = this.querySelectorAll('[data-gallery-slide]');
    if (!slides.length) return;

    if (idx < 0) idx = slides.length - 1;
    if (idx >= slides.length) idx = 0;

    this.#current = idx;

    slides.forEach((slide, slideIndex) => {
      const isActive = slideIndex === idx;
      slide.classList.toggle('is-active', isActive);
      slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
    });

    this.querySelectorAll('[data-gallery-thumb]').forEach((control) => {
      const controlIdx = parseInt(control.getAttribute('data-gallery-thumb') || '-1', 10);
      const isActive = controlIdx === idx;
      control.classList.toggle('is-active', isActive);

      if (control.matches('.pmd-gallery__dot')) {
        control.setAttribute('aria-current', isActive ? 'true' : 'false');
      }
    });
  }
}

if (!customElements.get('pmd-gallery')) {
  customElements.define('pmd-gallery', PmdGallery);
}
